import { useState } from "react";
import "./AIChatSidebar.css";

export default function AIChatSidebar({
  conversations,
  activeConversation,
  onSelectConversation,
  onNewConversation,
  onDeleteConversation,
  type,
  isCollapsed,
  onToggleCollapse,
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  const filteredConversations = conversations.filter((conv) =>
    conv.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const groupConversationsByDate = (convs) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const lastWeek = new Date(today);
    lastWeek.setDate(lastWeek.getDate() - 7);
    const lastMonth = new Date(today);
    lastMonth.setMonth(lastMonth.getMonth() - 1);

    const groups = {
      today: [],
      yesterday: [],
      lastWeek: [],
      lastMonth: [],
      older: [],
    };

    convs.forEach((conv) => {
      const convDate = new Date(conv.lastMessageAt || conv.createdAt);
      if (convDate.toDateString() === today.toDateString()) {
        groups.today.push(conv);
      } else if (convDate.toDateString() === yesterday.toDateString()) {
        groups.yesterday.push(conv);
      } else if (convDate > lastWeek) {
        groups.lastWeek.push(conv);
      } else if (convDate > lastMonth) {
        groups.lastMonth.push(conv);
      } else {
        groups.older.push(conv);
      }
    });

    return groups;
  };

  const groupedConversations = groupConversationsByDate(filteredConversations);

  const getTypeIcon = () => {
    switch (type) {
      case "chatbot":
        return (
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
          </svg>
        );
      case "advogado":
        return (
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z" />
          </svg>
        );
      case "jornalista":
        return (
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
          </svg>
        );
      default:
        return null;
    }
  };

  const getTypeName = () => {
    switch (type) {
      case "chatbot":
        return "ChatBot";
      case "advogado":
        return "Advogado";
      case "jornalista":
        return "Jornalista";
      default:
        return "AI";
    }
  };

  const handleDelete = (e, convId) => {
    e.stopPropagation();
    setShowDeleteConfirm(convId);
  };

  const confirmDelete = (convId) => {
    onDeleteConversation(convId);
    setShowDeleteConfirm(null);
  };

  return (
    <div className={`ai_sidebar ${isCollapsed ? "collapsed" : ""}`}>
      {/* Header */}
      <div className="ai_sidebar_header">
        <button className="toggle_sidebar_btn" onClick={onToggleCollapse}>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
          </svg>
        </button>

        {!isCollapsed && (
          <button className="new_chat_btn" onClick={onNewConversation}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14" />
            </svg>
            <span>Novo Chat</span>
          </button>
        )}
      </div>

      {!isCollapsed && (
        <>
          {/* Search */}
          <div className="ai_sidebar_search">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
            </svg>
            <input
              type="text"
              placeholder="Pesquisar conversas..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Conversations List */}
          <div className="ai_sidebar_conversations">
            {groupedConversations.today.length > 0 && (
              <div className="conversation_group">
                <h3 className="group_title">Hoje</h3>
                {groupedConversations.today.map((conv) => (
                  <ConversationItem
                    key={conv._id}
                    conversation={conv}
                    isActive={activeConversation?._id === conv._id}
                    onSelect={() => onSelectConversation(conv)}
                    onDelete={(e) => handleDelete(e, conv._id)}
                    showDeleteConfirm={showDeleteConfirm === conv._id}
                    onConfirmDelete={() => confirmDelete(conv._id)}
                    onCancelDelete={() => setShowDeleteConfirm(null)}
                  />
                ))}
              </div>
            )}

            {groupedConversations.yesterday.length > 0 && (
              <div className="conversation_group">
                <h3 className="group_title">Ontem</h3>
                {groupedConversations.yesterday.map((conv) => (
                  <ConversationItem
                    key={conv._id}
                    conversation={conv}
                    isActive={activeConversation?._id === conv._id}
                    onSelect={() => onSelectConversation(conv)}
                    onDelete={(e) => handleDelete(e, conv._id)}
                    showDeleteConfirm={showDeleteConfirm === conv._id}
                    onConfirmDelete={() => confirmDelete(conv._id)}
                    onCancelDelete={() => setShowDeleteConfirm(null)}
                  />
                ))}
              </div>
            )}

            {groupedConversations.lastWeek.length > 0 && (
              <div className="conversation_group">
                <h3 className="group_title">Últimos 7 dias</h3>
                {groupedConversations.lastWeek.map((conv) => (
                  <ConversationItem
                    key={conv._id}
                    conversation={conv}
                    isActive={activeConversation?._id === conv._id}
                    onSelect={() => onSelectConversation(conv)}
                    onDelete={(e) => handleDelete(e, conv._id)}
                    showDeleteConfirm={showDeleteConfirm === conv._id}
                    onConfirmDelete={() => confirmDelete(conv._id)}
                    onCancelDelete={() => setShowDeleteConfirm(null)}
                  />
                ))}
              </div>
            )}

            {groupedConversations.lastMonth.length > 0 && (
              <div className="conversation_group">
                <h3 className="group_title">Últimos 30 dias</h3>
                {groupedConversations.lastMonth.map((conv) => (
                  <ConversationItem
                    key={conv._id}
                    conversation={conv}
                    isActive={activeConversation?._id === conv._id}
                    onSelect={() => onSelectConversation(conv)}
                    onDelete={(e) => handleDelete(e, conv._id)}
                    showDeleteConfirm={showDeleteConfirm === conv._id}
                    onConfirmDelete={() => confirmDelete(conv._id)}
                    onCancelDelete={() => setShowDeleteConfirm(null)}
                  />
                ))}
              </div>
            )}

            {groupedConversations.older.length > 0 && (
              <div className="conversation_group">
                <h3 className="group_title">Mais antigas</h3>
                {groupedConversations.older.map((conv) => (
                  <ConversationItem
                    key={conv._id}
                    conversation={conv}
                    isActive={activeConversation?._id === conv._id}
                    onSelect={() => onSelectConversation(conv)}
                    onDelete={(e) => handleDelete(e, conv._id)}
                    showDeleteConfirm={showDeleteConfirm === conv._id}
                    onConfirmDelete={() => confirmDelete(conv._id)}
                    onCancelDelete={() => setShowDeleteConfirm(null)}
                  />
                ))}
              </div>
            )}

            {filteredConversations.length === 0 && (
              <div className="no_conversations">
                <div className="no_conversations_icon">{getTypeIcon()}</div>
                <p>Nenhuma conversa encontrada</p>
                <span>Comece uma nova conversa com o IA {getTypeName()}</span>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="ai_sidebar_footer">
            <div className="ai_type_badge">
              {getTypeIcon()}
              <span>IA {getTypeName()}</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ConversationItem({
  conversation,
  isActive,
  onSelect,
  onDelete,
  showDeleteConfirm,
  onConfirmDelete,
  onCancelDelete,
}) {
  return (
    <div
      className={`conversation_item ${isActive ? "active" : ""}`}
      onClick={onSelect}
    >
      <div className="conversation_icon">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
        </svg>
      </div>
      <div className="conversation_info">
        <span className="conversation_title">{conversation.title}</span>
      </div>
      <button
        className="conversation_delete"
        onClick={onDelete}
        title="Excluir conversa"
      >
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
        </svg>
      </button>

      {showDeleteConfirm && (
        <div className="delete_confirm_popup">
          <p>Excluir conversa?</p>
          <div className="delete_confirm_actions">
            <button className="cancel_delete" onClick={onCancelDelete}>
              Cancelar
            </button>
            <button className="confirm_delete" onClick={onConfirmDelete}>
              Excluir
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
